/*
 * AdminLegal — Phase CC-1
 * Unified GDPR / Privacy / Deletion / DMCA queue with SLA tracking.
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { Gavel, Mail, Trash2, ShieldAlert, Loader2, CheckCircle2 } from "lucide-react";


const KIND_META = {
  privacy:  { icon: Mail,        label: "Privacy",  color: "sky" },
  deletion: { icon: Trash2,      label: "Deletion", color: "rose" },
  dmca:     { icon: ShieldAlert, label: "DMCA",     color: "amber" },
};

const STATUS_OPTIONS = [
  { value: "fulfilled",   label: "Fulfilled" },
  { value: "rejected",    label: "Rejected" },
  { value: "needs_info",  label: "Needs more info" },
];


export default function AdminLegal() {
  const [data, setData] = useState({ rows: [], summary: {}, sla_days: 30 });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");           // "" | "open" | …
  const [selected, setSelected] = useState(null);     // active decide dialog target

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = filter ? `?status=${encodeURIComponent(filter)}` : "";
      const r = await api.get(`/admin/legal-queue${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load legal queue");
    } finally { setBusy(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const s = data.summary || {};
  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-legal">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Gavel className="w-5 h-5"/>Legal Queue
        </h1>
        <p className="text-[12.5px] text-foreground/55">
          Privacy / Deletion / DMCA requests with a {data.sla_days}-day SLA. Overdue items are flagged in rose.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5">
        <Kpi label="Total" value={s.total ?? 0} testid="legal-kpi-total"/>
        <Kpi label="Open" value={s.open ?? 0} tone={s.open > 0 ? "amber" : "neutral"}
              testid="legal-kpi-open"/>
        <Kpi label="Overdue" value={s.overdue ?? 0}
              tone={s.overdue > 0 ? "rose" : "emerald"}
              testid="legal-kpi-overdue"/>
        <Kpi label="Privacy" value={s.privacy ?? 0} testid="legal-kpi-privacy"/>
        <Kpi label="Deletion" value={s.deletion ?? 0} testid="legal-kpi-deletion"/>
        <Kpi label="DMCA" value={s.dmca ?? 0} testid="legal-kpi-dmca"/>
      </div>

      <div className="flex items-center gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="h-9 px-2 rounded-md border bg-background text-[13px]"
          data-testid="legal-filter">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="pending">Pending</option>
          <option value="fulfilled">Fulfilled</option>
          <option value="rejected">Rejected</option>
        </select>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="legal-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>

      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="legal-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2">Received</th>
              <th className="px-3 py-2">SLA</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => {
              const meta = KIND_META[r.kind] || { icon: Gavel, label: r.kind, color: "neutral" };
              const Icon = meta.icon;
              const overdue = (r.sla_days_left ?? 999) < 0
                              && (r.status === "open" || !r.status || r.status === "pending");
              const subject = r.email || r.user_email || r.complainant?.email || r.rights_holder?.name || r.id;
              return (
                <tr key={`${r.kind}-${r.id}`} data-testid={`legal-row-${r.id}`}
                    className={overdue ? "bg-rose-50/40" : ""}>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <Icon className={`w-3.5 h-3.5 text-${meta.color}-600`}/>
                      {meta.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">{subject}</td>
                  <td className="px-3 py-2 text-foreground/65 text-[11px] mono">
                    {r.received_at ? new Date(r.received_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <SlaChip days={r.sla_days_left} status={r.status}/>
                  </td>
                  <td className="px-3 py-2">
                    <span className="chip text-[10.5px] bg-foreground/5 border-foreground/10">
                      {r.status || "open"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="sm"
                      onClick={() => setSelected(r)}
                      data-testid={`legal-decide-${r.id}`}>
                      Decide
                    </Button>
                  </td>
                </tr>
              );
            })}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-foreground/45">No requests</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <DecideDialog row={selected} onClose={() => setSelected(null)}
        onSaved={() => { setSelected(null); load(); }}/>
    </div>
  );
}


function SlaChip({ days, status }) {
  if (status && !["open", "pending"].includes(status)) {
    return <span className="text-[11px] text-foreground/55">—</span>;
  }
  if (days == null) return <span className="text-[11px] text-foreground/55">—</span>;
  if (days < 0) {
    return <span className="chip text-[10.5px] bg-rose-50 border-rose-200 text-rose-800">
      Overdue {-days}d
    </span>;
  }
  if (days <= 7) {
    return <span className="chip text-[10.5px] bg-amber-50 border-amber-200 text-amber-800">
      {days}d left
    </span>;
  }
  return <span className="chip text-[10.5px] bg-emerald-50 border-emerald-200 text-emerald-800">
    {days}d left
  </span>;
}


function Kpi({ label, value, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-foreground/3 border-foreground/10",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] text-foreground/55 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}


function DecideDialog({ row, onClose, onSaved }) {
  const [decision, setDecision] = useState("fulfilled");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!row;

  useEffect(() => { if (row) { setDecision("fulfilled"); setReason(""); } }, [row]);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars · audit-logged)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/legal-queue/${row.kind}/${row.id}/decide`, {
        decision, reason: reason.trim(),
      });
      toast.success(`Marked as ${decision}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record decision");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="legal-decide-dialog">
        <DialogHeader>
          <DialogTitle>Decide {row?.kind} request</DialogTitle>
          <DialogDescription>
            Audit-logged via the hash-chained admin_overrides collection.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="ld-decision">Decision</Label>
            <select id="ld-decision" value={decision} onChange={(e) => setDecision(e.target.value)}
              className="w-full h-9 px-2 rounded-md border bg-background text-[13px]"
              data-testid="legal-decision-select">
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ld-reason">Internal note (min 3 chars · audit-logged)</Label>
            <Textarea id="ld-reason" rows={3} value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="legal-decide-reason"
              placeholder="e.g. all user data scrubbed across users/orders/wallet_txns"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3}
            data-testid="legal-decide-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin"/>
                  : <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>}
            Record decision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
